// AI drawing screening — Phase 5 review tab. Read + confirm/edit/reject
// over a run's proposed systems. NO promotion to project_systems here
// (Phase 6). Admin-only: the tab itself is hidden from non-admins
// (every screening endpoint is requireAdmin), this component assumes
// the caller gated it.
//
// Triage order is ip_basis, NOT confidence (measured 0.93–1.0 across a
// whole run — not a signal): unclassifiable (needs a human) →
// explicit_ip (highest consequence) → no_data_connections (soft No) →
// explicit_serial (solid No, bulk-confirmable).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ContextMenu, ContextMenuItem } from '@/components/ui/ContextMenu'
import { ArrowLeft, ChevronDown, ChevronRight, AlertTriangle, CheckCircle, Clock, XCircle, Pencil, UploadCloud, FileText, FileClock, Loader2, RefreshCw } from 'lucide-react'
import { projectsApi, ScreeningRunRow, ScreeningSystemRow } from '../../lib/projectsApi'
import { chunkedUpload } from '../../lib/chunkedUpload'
import { API_BASE_URL, fetchWithAuthRetry } from '../../lib/apiClient'
import { useToast } from '../../contexts/ToastContext'
import { Button } from '../ui/Button'
import { useTranslation } from 'react-i18next'
import { hasTitleAssociationNote, hasUsableScreeningInformation } from '@/lib/screeningPresentation'
import { screeningReviewItems } from '@/lib/screening/reviewGroups'

// ── excel-style column filters ──────────────────────────────────────────
// AND across columns, OR within a column's selected values. '(blank)'
// selects rows where the field is empty.
type MultiFilterKey = 'ip' | 'target' | 'ip_basis' | 'scope_class' | 'cbs' | 'status' | 'ship_function' | 'maker'
const MULTI_KEYS: MultiFilterKey[] = ['ip', 'target', 'ip_basis', 'scope_class', 'cbs', 'status', 'ship_function', 'maker']
const FILTER_LABEL: Record<MultiFilterKey, string> = {
  ip: 'IP', target: 'Target', ip_basis: 'Basis', scope_class: 'Scope',
  cbs: 'CBS', status: 'Status', ship_function: 'Function', maker: 'Maker',
}
const emptyFilters = (): Record<MultiFilterKey, Set<string>> =>
  Object.fromEntries(MULTI_KEYS.map(k => [k, new Set<string>()])) as Record<MultiFilterKey, Set<string>>

const fieldValue = (row: ScreeningSystemRow, key: MultiFilterKey): string =>
  String((row as any)[key] ?? '').trim()

function FilterDropdown({ label, options, selected, onToggle }: {
  label: string
  options: string[]
  selected: Set<string>
  onToggle: (value: string) => void
}) {
  const { t } = useTranslation()
  const active = selected.size > 0
  return (
    <details className="relative">
      <summary className={
        'list-none cursor-pointer select-none text-[11px] px-2 py-1 rounded border ' +
        (active
          ? 'border-blue-400 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700 font-medium'
          : 'border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-300')
      }>
        {label}{active ? ` (${selected.size})` : ''} ▾
      </summary>
      <div className="absolute left-0 top-full mt-1 z-30 min-w-[190px] max-h-64 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg p-1.5 space-y-0.5">
        {options.map(o => (
          <label key={o} className="flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300 px-1 py-0.5 rounded hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer">
            <input type="checkbox" className="h-3 w-3 accent-blue-500" checked={selected.has(o)} onChange={() => onToggle(o)} />
            <span className="truncate">{o === '' ? t('screening.blank') : o}</span>
          </label>
        ))}
      </div>
    </details>
  )
}

// Real wall time from the H3594 87-sheet evaluation, scaled by page count.
const EST_SECONDS_PER_PAGE = (9 * 60) / 87

// discipline hint from the filename — drives default selection.
// H3594 and HN5119 both put every IP=Yes system in NAVI/CONTROL/FIRE;
// POWER and LIGHTING rarely carry IP links (but CAN — shore
// connection, intelligent breakers, PMS — so they're default-off,
// never hidden).
type Discipline = 'NAVI' | 'CONTROL' | 'POWER' | 'FIRE' | 'LIGHTING' | null
// ORDER MATTERS — real filenames carry several keywords at once, and the
// first match wins. "WIRING DIAGRAM & ARRANGEMENT OF FIRE & GENERAL
// ALARM" holds both FIRE and ALARM; "AUTO PLANT (MACH.)" holds neither
// CONTROL nor a discipline word HHI would recognise. Every pattern below
// comes from a filename actually seen on H3594, HN5119, H3542, SN2754
// or N294 — not from guesses.
const DISCIPLINE_PATTERNS: Array<[RegExp, Exclude<Discipline, null>]> = [
  // FIRE first: its files also say "GENERAL ALARM", and an alarm system
  // is not a lighting or power volume
  [/FIRE|FDS|GEN(ERAL)?\.?\s*ALARM/i, 'FIRE'],
  // NAVI before CONTROL: HHI's navigation file is "COMMUNICATION AND
  // NAVIGATION EQUIP", HJSC's is "NAVIGATION & INTERCOM"
  [/NAVI|NAVIGATION|INTERCOM|COMMUNICATION|\bCOMM\b/i, 'NAVI'],
  // the control/automation family, across four yards' spellings:
  // HHI "CONTROL & INSTRUMENTATION", HJSC "AUTO PLANT (MACH.)" and
  // "AUTO PLANT (CARGO)", SHI "AUTOMATION SYTSEM (MACHINERY PART)"
  // (yes, misspelled on the real file) and "ACD(CARGO PART)"
  [/CONTROL|CTRL|AUTO\s*PLANT|AUTOMATION|AUTO\s*SYST?EM|INSTRUMENT|I&C|\bACD\b|MACH(INERY|\.)|CARGO/i, 'CONTROL'],
  [/LIGHT/i, 'LIGHTING'],
  [/POWER|PWR|ELEC(TRIC)?\.?\s*POWER/i, 'POWER'],
]
const LOW_YIELD: Discipline[] = ['POWER', 'LIGHTING']
export function classifyDiscipline(filename: string): Discipline {
  for (const [re, label] of DISCIPLINE_PATTERNS) {
    if (re.test(filename)) return label
  }
  // unrecognized files stay default-CHECKED (screening an unknown beats
  // skipping it silently) — but say so, so the list above can grow from
  // real filenames instead of guesses
  console.info(`[screening] filename matched no discipline pattern — defaulting to screened: "${filename}"`)
  return null
}
function disciplineBadge(d: Discipline) {
  if (!d) return null
  const low = LOW_YIELD.includes(d)
  return (
    <span className={
      'text-[10px] font-medium px-1.5 py-0.5 rounded ' +
      (low
        ? 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300')
    }>{d}</span>
  )
}

const BASIS_ORDER: Record<string, number> = {
  unclassifiable: 0, explicit_ip: 1, default_serial_untyped_pair: 2, no_data_connections: 3,
  explicit_serial: 4, explicit_hardwired: 5, internal_ip_only: 6,
}

// keyed loosely on purpose: the server can add a basis (the rule-default
// one did) without the shared API type having to move in lockstep
const BASIS_LABEL: Record<string, string> = {
  unclassifiable: 'needs review',
  explicit_ip: 'IP cited',
  default_serial_untyped_pair: 'by rule: untyped pair',
  no_data_connections: 'no data conn.',
  explicit_serial: 'serial cited',
  explicit_hardwired: 'hardwired',
  internal_ip_only: 'internal IP only',
}

// a row resolved by RULE is not a row resolved by EVIDENCE — it reads
// differently on purpose, and sorts just after the ones needing review
const BASIS_TITLE: Record<string, string> = {
  default_serial_untyped_pair: 'No · rule default: twisted pair, no protocol stated on drawing',
}

function basisBadge(basis: string | null) {
  if (!basis) return null
  if (!BASIS_LABEL[basis]) return null   // an unknown basis shows nothing rather than an empty chip
  const cls =
    basis === 'unclassifiable'      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
    basis === 'explicit_ip'         ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
    basis === 'default_serial_untyped_pair' ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 ring-1 ring-inset ring-violet-300/60 dark:ring-violet-700/60' :
    basis === 'no_data_connections' ? 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400' :
    basis === 'internal_ip_only'    ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' :
                                      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'  // serial + hardwired: solid non-IP
  return (
    <span title={BASIS_TITLE[basis]} className={`text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${cls}`}>
      {BASIS_LABEL[basis]}
    </span>
  )
}

// Which of the three outcomes a row belongs to. Components and
// infrastructure are not assessed systems, so they sit apart rather
// than diluting the counts.
function bucketOf(r: ScreeningSystemRow): 'target' | 'further' | 'unknown' | 'noip' | 'scope' {
  if (r.scope_class === 'component' || r.scope_class === 'infrastructure') return 'scope'
  if (r.target === 'Selected') return 'target'
  if (r.target === 'Further Review') return 'further'
  // A BLANK ip IS NOT A NO. This used to fall through to 'noip', which
  // put 205 unassessed SN2754 systems — the diesel generators, the
  // steering gear, the ballast and cargo pumps — under a heading that
  // asserted the very thing nobody had determined. The row's own pill
  // showed "—" correctly, and the heading overrode it at a glance.
  // "No IP" now means only what we actually determined.
  if (!r.ip) return 'unknown'
  return 'noip'
}

// why a row was never determined — shown per row in the Not assessed
// section, so the heading is not the only thing explaining itself
export function notAssessedReason(r: ScreeningSystemRow): string {
  if (r.status === 'pending_maker_drawing') {
    return r.fail_reason?.trim() || 'the maker drawing has not been issued'
  }
  if (r.status === 'failed') {
    return r.fail_reason?.trim() || 'screening did not complete for this system'
  }
  return 'not screened — no IP determination was reached'
}

function ipPill(ip: string | null) {
  if (ip === 'Yes')            return <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">IP: Yes</span>
  if (ip === 'No')             return <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">IP: No</span>
  if (ip === 'Further Review') return <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Further Review</span>
  return <span className="text-[11px] text-slate-400">—</span>
}

function statusIcon(status: ScreeningSystemRow['status']) {
  if (status === 'confirmed') return <CheckCircle className="h-3.5 w-3.5 text-green-500" aria-label="confirmed" />
  if (status === 'edited')    return <Pencil className="h-3.5 w-3.5 text-blue-500" aria-label="edited + confirmed" />
  if (status === 'rejected')  return <XCircle className="h-3.5 w-3.5 text-red-400" aria-label="rejected" />
  if (status === 'failed')    return <AlertTriangle className="h-3.5 w-3.5 text-red-500" aria-label="failed" />
  if (status === 'pending_maker_drawing') return <FileClock className="h-3.5 w-3.5 text-violet-500" aria-label="drawing not yet issued" />
  return <Clock className="h-3.5 w-3.5 text-amber-500" aria-label="proposed" />
}

const EDIT_FIELDS = [
  { key: 'system_name',      labelKey: 'systemName', type: 'text' },
  { key: 'maker',            labelKey: 'maker',       type: 'text' },
  { key: 'model',            labelKey: 'model',       type: 'text' },
  { key: 'cbs',              labelKey: 'cbs',         type: 'select' },
  { key: 'ship_function',    labelKey: 'shipFunction', type: 'text' },
  { key: 'ip',               labelKey: 'ip',          type: 'select' },
  { key: 'connected_system', labelKey: 'connectedSystem', type: 'textarea' },
  { key: 'detail',           labelKey: 'detail',      type: 'textarea' },
  { key: 'remark',           labelKey: 'remark',      type: 'text' },
] as const
type EditKey = typeof EDIT_FIELDS[number]['key']

export function ScreeningTab({ projectId, droppedFiles, onDroppedConsumed, onBackToCompliance }: {
  projectId: string
  // files routed in by the page-level drop router (PDFs dropped
  // anywhere on the project page) — consumed once by the upload panel
  droppedFiles?: File[] | null
  onDroppedConsumed?: () => void
  onBackToCompliance: () => void
}) {
  const { t } = useTranslation()
  const { success, error: showError } = useToast()
  const [runs, setRuns] = useState<ScreeningRunRow[]>([])
  // a run in review or processing is the vessel's LIVE one — new
  // drawings extend it rather than opening a parallel result set
  const hasLiveRun = runs.some(r => r.status === 'review' || r.status === 'processing')
  // NOT hasLiveRun. That means "there is a run to attach an upload to",
  // which includes 'review' — the normal resting state of a FINISHED
  // run, and the state 10 of 12 runs are in. Gating the re-screen
  // buttons on it disabled them on almost every project, which is the
  // same mistake the old failed-only button made: an action that exists
  // but is never available. Only 'processing' means work is in flight.
  const isScreening = runs.some(r => r.status === 'processing')
  const [runId, setRunId] = useState<string | null>(null)
  const [systems, setSystems] = useState<ScreeningSystemRow[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [busyId, setBusyId] = useState<string | null>(null)
  const [editRow, setEditRow] = useState<ScreeningSystemRow | null>(null)
  const [rejectRow, setRejectRow] = useState<ScreeningSystemRow | null>(null)
  const [promotePreview, setPromotePreview] = useState<Awaited<ReturnType<typeof projectsApi.promoteScreening>> | null>(null)
  const [promotedCount, setPromotedCount] = useState<number | null>(null)
  const [failedDrawings, setFailedDrawings] = useState<Array<{ original_filename: string | null; fail_reason: string | null }>>([])
  // the drawing currently being retried, so its button alone shows busy
  const [rescreening, setRescreening] = useState<string | null>(null)
  const [drawingMenu, setDrawingMenu] = useState<{ x: number; y: number; items: ContextMenuItem[] } | null>(null)

  // the project's drawing files, aggregated across runs (drawings are
  // persisted with their run; dedup by name+size keeps the newest)
  const [drawings, setDrawings] = useState<Array<{
    id?: string; original_filename: string | null; file_size_bytes: number; page_count: number | null
    status: string; fail_reason: string | null; uploaded_at: string
  }>>([])
  // drawing_id → filename, so a cited page can name its drawing in the viewer
  const drawingNames = useMemo(
    () => new Map(drawings.filter(d => d.id).map(d => [d.id as string, d.original_filename ?? 'drawing'])),
    [drawings],
  )
  const pollRef = useRef<number | null>(null)

  const loadRuns = useCallback(async () => {
    try {
      const rs = await projectsApi.listScreeningRuns(projectId)
      setRuns(rs)
      // keep the current selection when it still exists; else newest run
      setRunId(cur => (cur && rs.some(r => r.id === cur)) ? cur : (rs[0]?.id ?? null))
    } catch (err: any) {
      showError(err?.message ?? 'Failed to load screening runs')
    }
  }, [projectId, showError])

  const loadSystems = useCallback(async (rid: string) => {
    setLoading(true)
    try {
      setSystems(await projectsApi.listScreeningSystems(rid))
    } catch (err: any) {
      showError(err?.message ?? 'Failed to load screening systems')
    } finally {
      setLoading(false)
    }
  }, [showError])

  useEffect(() => { loadRuns() }, [loadRuns])
  useEffect(() => { if (runId) loadSystems(runId) }, [runId, loadSystems])

  // aggregate the drawing files across all runs (newest first, deduped)
  useEffect(() => {
    if (runs.length === 0) { setDrawings([]); return }
    let cancelled = false
    ;(async () => {
      try {
        const details = await Promise.all(runs.map(r => projectsApi.getScreeningRun(r.id)))
        if (cancelled) return
        const seen = new Map<string, { id?: string; original_filename: string | null; file_size_bytes: number; page_count: number | null; status: string; fail_reason: string | null; uploaded_at: string }>()
        for (const d of details) {
          for (const dr of d.drawings ?? []) {
            // identity is the CONTENT, not the name — the same volume
            // arrived under two names on N294 and showed up twice here.
            // name+size stays as the fallback for older API responses.
            const hash = (dr as { content_hash?: string | null }).content_hash
            const key = hash ? `sha:${hash}` : `${dr.original_filename}|${dr.file_size_bytes}`
            if (!seen.has(key)) {
              seen.set(key, {
                id: (dr as { id?: string }).id,
                original_filename: dr.original_filename, file_size_bytes: dr.file_size_bytes,
                page_count: dr.page_count, status: dr.status, fail_reason: dr.fail_reason ?? null,
                uploaded_at: d.created_at,
              })
            }
          }
        }
        setDrawings([...seen.values()])
      } catch { /* drawings list is cosmetic; runs/table remain usable */ }
    })()
    return () => { cancelled = true }
  }, [runs])

  const run = runs.find(r => r.id === runId) ?? null

  // live progress: while the selected run is processing, poll it — a
  // 9-minute background job must never look frozen. On completion,
  // reload the systems table and surface any failed drawings.
  useEffect(() => {
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null }
    if (!runId || run?.status !== 'processing') return
    pollRef.current = window.setInterval(async () => {
      try {
        const fresh = await projectsApi.getScreeningRun(runId)
        setRuns(prev => prev.map(r => r.id === runId ? { ...r, ...fresh, drawings: undefined } as ScreeningRunRow : r))
        setFailedDrawings((fresh.drawings ?? []).filter(d => d.status === 'failed').map(d => ({ original_filename: d.original_filename, fail_reason: d.fail_reason })))
        if (fresh.status !== 'processing') {
          window.clearInterval(pollRef.current!); pollRef.current = null
          loadSystems(runId)
        }
      } catch { /* transient poll failure — next tick retries */ }
    }, 4000)
    return () => { if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null } }
  }, [runId, run?.status, loadSystems])

  const [multiFilters, setMultiFilters] = useState<Record<MultiFilterKey, Set<string>>>(emptyFilters)
  // the one control people actually reach for, kept out of the drawer
  const [outcome, setOutcome] = useState<'all' | 'target' | 'further' | 'unknown' | 'noip'>('all')
  const [showMore, setShowMore] = useState(false)
  // NO IP and NOT IN SCOPE start closed — the assessment is read
  // top-down: what was selected, then what needs deciding
  const [closedSections, setClosedSections] = useState<Set<string>>(new Set(['unknown', 'noip', 'scope', 'empty']))
  // every row starts CLOSED. Opening the IP=Yes rows automatically
  // buried the table under evidence panels — the reader wants the list
  // first and the proof on demand. "show all evidence" opens them.
  const [showAllEvidence, setShowAllEvidence] = useState(false)
  const [nameQuery, setNameQuery] = useState('')
  const [mismatchOnly, setMismatchOnly] = useState(false)

  const toggleFilter = (key: MultiFilterKey, value: string) => setMultiFilters(prev => {
    const next = { ...prev, [key]: new Set(prev[key]) }
    if (next[key].has(value)) next[key].delete(value); else next[key].add(value)
    return next
  })
  const activeFilterCount = MULTI_KEYS.reduce((n, k) => n + (multiFilters[k].size > 0 ? 1 : 0), 0)
    + (nameQuery ? 1 : 0) + (mismatchOnly ? 1 : 0)
  const clearFilters = () => { setMultiFilters(emptyFilters()); setNameQuery(''); setMismatchOnly(false) }

  const allRows = useMemo(() => systems.filter(s => s.is_system && hasUsableScreeningInformation(s)), [systems])
  const emptyRows = useMemo(() => systems.filter(s => s.is_system && !hasUsableScreeningInformation(s)), [systems])


  // distinct value lists for the enumerable filters, from this run
  const filterOptions = useMemo(() => {
    const opts = {} as Record<MultiFilterKey, string[]>
    for (const key of MULTI_KEYS) {
      const vals = new Set<string>()
      for (const r of allRows) vals.add(fieldValue(r, key))
      opts[key] = [...vals].sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
    }
    return opts
  }, [allRows])

  const rows = useMemo(() => {
    // filters: AND across columns, OR within a column (Excel autofilter
    // semantics). the TRIAGE sort stays the default — filtering never
    // silently reorders: in-scope → components → infrastructure, by
    // ip_basis within each group
    const SCOPE_ORDER: Record<string, number> = { system: 0, component: 1, infrastructure: 2 }
    const q = nameQuery.trim().toLowerCase()
    const filtered = allRows.filter(r => {
      for (const key of MULTI_KEYS) {
        const sel = multiFilters[key]
        if (sel.size > 0 && !sel.has(fieldValue(r, key))) return false
      }
      // search covers system AND maker — the two things anyone looks up
      if (q && !`${r.system_name} ${r.maker ?? ''}`.toLowerCase().includes(q)) return false
      if (mismatchOnly && !hasTitleAssociationNote(r)) return false
      if (outcome !== 'all' && bucketOf(r) !== outcome) return false
      return true
    })
    return filtered.sort((a, b) =>
      (SCOPE_ORDER[a.scope_class ?? 'system'] - SCOPE_ORDER[b.scope_class ?? 'system'])
      || (BASIS_ORDER[a.ip_basis ?? 'unclassifiable'] - BASIS_ORDER[b.ip_basis ?? 'unclassifiable'])
      || ((a.item_no ?? 999) - (b.item_no ?? 999)))
  }, [allRows, multiFilters, nameQuery, mismatchOnly, outcome])

  // the three outcomes the assessment is actually read by, plus the
  // rows that are not assessed systems at all
  const sections = useMemo(() => ([
    { key: 'target'  as const, label: 'Target systems',  rows: rows.filter(r => bucketOf(r) === 'target') },
    { key: 'further' as const, label: 'Further review',  rows: rows.filter(r => bucketOf(r) === 'further') },
    // between Further review and No IP: these are not an outcome, they
    // are the absence of one, and they must not read as either
    { key: 'unknown' as const, label: 'Not assessed',    rows: rows.filter(r => bucketOf(r) === 'unknown') },
    { key: 'noip'    as const, label: 'No IP',           rows: rows.filter(r => bucketOf(r) === 'noip') },
    { key: 'scope'   as const, label: 'Not in scope',    rows: rows.filter(r => bucketOf(r) === 'scope') },
    { key: 'empty' as const, label: t('screening.noUsableInformation'), rows: emptyRows.filter(r => {
      if (outcome !== 'all' && outcome !== 'unknown') return false
      if (mismatchOnly && !hasTitleAssociationNote(r)) return false
      const q = nameQuery.trim().toLowerCase()
      if (q && !`${r.system_name} ${r.maker ?? ''}`.toLowerCase().includes(q)) return false
      return MULTI_KEYS.every(key => multiFilters[key].size === 0 || multiFilters[key].has(fieldValue(r, key)))
    }) },
  ]), [rows, emptyRows, outcome, mismatchOnly, nameQuery, multiFilters, t])

  // ── export grouping ────────────────────────────────────────────────
  // Export packaging remains separate from conservative review grouping.
  // The table groups compatible source rows without merging their decisions
  // or evidence; every source occurrence remains independently reviewable.
  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeData, setMergeData] = useState<Awaited<ReturnType<typeof projectsApi.screeningMergeGroups>> | null>(null)

  const loadMerge = async () => {
    if (!runId) return
    try { setMergeData(await projectsApi.screeningMergeGroups(runId)) }
    catch (err: any) { showError(err?.message ?? 'could not load export grouping') }
  }
  const openMergePanel = async () => {
    setBusyId('merge'); setMergeOpen(true)
    await loadMerge()
    setBusyId(null)
  }
  const reanalyse = async () => {
    if (!runId) return
    setBusyId('merge')
    try {
      await projectsApi.analyseScreeningMerge(runId)
      await loadMerge()
    } catch (err: any) {
      // a failed pass is reported, never silently treated as "nothing merged"
      showError(err?.message ?? 'the merge pass failed — the previous grouping is unchanged')
      await loadMerge()
    } finally { setBusyId(null) }
  }
  const splitGroup = async (groupId: string) => {
    try { await projectsApi.splitMergeGroup(groupId); await loadMerge() }
    catch (err: any) { showError(err?.message ?? 'could not split the group') }
  }
  const dropMember = async (groupId: string, rowId: string) => {
    try { await projectsApi.excludeFromMergeGroup(groupId, rowId); await loadMerge() }
    catch (err: any) { showError(err?.message ?? 'could not remove the row') }
  }

  // export EXACTLY the filtered view — server regenerates from source
  // data with the same filter semantics and audits the egress.
  // What comes back is the VENDOR CONTACT REQUEST form, not an
  // assessment deliverable: see server/src/lib/contactRequestWorkbook.ts
  const exportXlsx = async () => {
    if (!runId) return
    setBusyId('export')
    try {
      const params = new URLSearchParams()
      for (const key of MULTI_KEYS) {
        if (multiFilters[key].size > 0) params.set(key, [...multiFilters[key]].map(v => v === '' ? '(blank)' : v).join(','))
      }
      if (nameQuery.trim()) params.set('q', nameQuery.trim())
      if (mismatchOnly) params.set('mismatch', '1')
      const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/ra-screening/${runId}/export?${params.toString()}`, { method: 'GET' })
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? `export failed: ${res.status}`)
      const blob = await res.blob()
      const cd = res.headers.get('Content-Disposition') ?? ''
      const fname = /filename="([^"]+)"/.exec(cd)?.[1] ?? 'vendor_contact_request.xlsx'
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = fname; a.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      showError(err?.message ?? 'Export failed')
    } finally {
      setBusyId(null)
    }
  }
  const nonSystems = useMemo(() => systems.filter(s => !s.is_system), [systems])
  // run-wide counts — actions operate on the whole run, never a filtered view
  const evidenceBackedBases = new Set([
    'explicit_ip', 'explicit_serial', 'explicit_hardwired', 'no_data_connections', 'internal_ip_only',
  ])
  const aiConfirmable = allRows.filter(r =>
    (r.scope_class ?? 'system') === 'system'
    && r.status === 'proposed'
    && Array.isArray(r.evidence)
    && r.evidence.length > 0
    && (
      evidenceBackedBases.has(r.ip_basis ?? '')
      || (r.cbs === 'Yes' && !!r.ship_function?.trim() && !/^N\/?A$/i.test(r.ship_function.trim()))
    )).length

  const confirmedUnpromoted = allRows.filter(r =>
    (r.scope_class ?? 'system') === 'system' && ['confirmed', 'edited'].includes(r.status) && !r.promoted_project_system_id).length

  const openPromotePreview = async () => {
    if (!runId) return
    setBusyId('promote')
    try {
      setPromotePreview(await projectsApi.promoteScreening(runId, true))
    } catch (err: any) {
      showError(err?.message ?? 'Preview failed')
    } finally {
      setBusyId(null)
    }
  }

  const executePromote = async () => {
    if (!runId) return
    setBusyId('promote')
    try {
      const result = await projectsApi.promoteScreening(runId, false)
      success(`Promoted: ${result.created} created, ${result.updated} updated in the register`)
      setPromotedCount(result.created + result.updated)
      setPromotePreview(null)
      await loadSystems(runId)
    } catch (err: any) {
      showError(err?.message ?? 'Promotion failed')
    } finally {
      setBusyId(null)
    }
  }

  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const applyPatch = async (row: ScreeningSystemRow, body: Parameters<typeof projectsApi.patchScreeningSystem>[1], okMsg: string) => {
    setBusyId(row.id)
    try {
      const updated = await projectsApi.patchScreeningSystem(row.id, body)
      setSystems(prev => prev.map(s => s.id === row.id ? updated : s))
      success(okMsg)
      // A review decision can make formerly compatible members differ.
      // Recompute non-destructive grouping from the current source rows.
      if (runId) await loadSystems(runId)
    } catch (err: any) {
      showError(err?.message ?? 'Action failed')
    } finally {
      setBusyId(null)
    }
  }

  const bulkConfirm = async () => {
    if (!runId) return
    setBusyId('bulk')
    try {
      const { confirmed, unresolved } = await projectsApi.bulkConfirmScreening(runId)
      success(`${confirmed} AI-screened result(s) accepted${unresolved ? `; ${unresolved} unresolved row(s) left for review` : ''}`)
      await loadSystems(runId)
    } catch (err: any) {
      showError(err?.message ?? 'Bulk confirm failed')
    } finally {
      setBusyId(null)
    }
  }

  const onRunCreated = (newRunId: string) => {
    setFailedDrawings([])
    // let the project page's tab badge know immediately (it polls only
    // while it believes a run is processing)
    window.dispatchEvent(new CustomEvent('seacuredb:screening-run-started', { detail: { runId: newRunId } }))
    // seed a processing run row immediately so polling starts before
    // the next runs refetch
    setRuns(prev => [{
      id: newRunId, project_id: projectId, model: '', prompt_version: null,
      status: 'processing', prompt_tokens: 0, completion_tokens: 0, cost_usd: 0,
      systems_total: 0, systems_done: 0, error_message: null,
      created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
    } as ScreeningRunRow, ...prev])
    setRunId(newRunId)
    setSystems([])
    loadRuns()
  }

  // BELOW onRunCreated, and it has to stay there. These read it in their
  // dependency arrays, and a dependency array is evaluated during render,
  // not when the callback fires — declared above it, the const is still in
  // its temporal dead zone and every render of this tab throws before it
  // paints. That blanked the whole wiring tab on every project.
  // Retry ONE drawing. The server replaces that drawing's rows and runs
  // it inside the existing run, so the drawings that already succeeded
  // are untouched and no second run appears beside this one.
  const rescreenDrawing = useCallback(async (drawingId: string, name: string | null) => {
    setRescreening(drawingId)
    try {
      const r = await projectsApi.rescreenDrawing(drawingId)
      success(
        'Re-screening started',
        `${name ?? 'Drawing'}${r.rows_replaced ? ` — ${r.rows_replaced} previous row(s) replaced` : ''}`,
      )
      onRunCreated?.(r.run_id)
    } catch (e: any) {
      showError('Could not re-screen', e?.message ?? 'unknown error')
    } finally {
      setRescreening(null)
    }
  }, [onRunCreated, success, showError])

  // Every drawing in the run, from scratch.
  //
  // The confirm is not ceremony. This discards every screened result in
  // the run, and a run can be hours of model time — SN2754's power
  // volume alone is 306 pages. So the dialog states the count, the cost
  // and the one thing that is NOT recoverable by waiting: any edit
  // someone made to a screened row.
  const rescreenAll = useCallback(async () => {
    if (!runId) return
    const n = drawings.length
    const screened = drawings.filter(d => d.status === 'screened').length
    if (!window.confirm(
      `Re-screen all ${n} drawing${n === 1 ? '' : 's'} in this run?

` +
      `Every screened result is discarded and rebuilt from the drawings — ` +
      `${screened} drawing${screened === 1 ? ' has' : 's have'} results that will be replaced, ` +
      `along with any edits made to those rows.

` +
      `Screening runs on the server and can take a while; you can leave this page.`,
    )) return
    setRescreening('ALL')
    try {
      const r = await projectsApi.rescreenRun(runId)
      success('Re-screening started',
        `${r.drawings} drawing(s)${r.rows_replaced ? ` — ${r.rows_replaced} previous row(s) discarded` : ''}`)
      onRunCreated?.(r.run_id)
    } catch (e: any) {
      showError('Could not re-screen the run', e?.message ?? 'unknown error')
    } finally {
      setRescreening(null)
    }
  }, [runId, drawings, onRunCreated, success, showError])

  // Right-click any drawing row. The button only appears on a failed or
  // empty drawing, and on four of six vessels every drawing is screened —
  // so for most projects there was nothing on screen to find. The menu is
  // always there, and says why a drawing failed where it did.
  const handleDrawingContextMenu = useCallback(
    (e: React.MouseEvent, d: { id?: string; original_filename: string | null; status: string; fail_reason: string | null }) => {
      e.preventDefault()
      const items: ContextMenuItem[] = []
      const retryable = d.status === 'failed' || d.status === 'no_systems'

      if (d.fail_reason) {
        // not an action — the reason, where the row has one, so the user
        // knows what they are retrying and whether a fix has landed since
        items.push({ label: d.fail_reason.slice(0, 120), onClick: () => {}, disabled: true })
      }

      items.push({
        label: retryable ? 'Re-screen this drawing' : 'Re-screen this drawing (already screened)',
        icon: <RefreshCw className="h-4 w-4" />,
        // the divider sits ABOVE this item, separating the reason from
        // the action; a divider-only entry would render an empty row
        divider: !!d.fail_reason,
        disabled: !d.id || rescreening === d.id,
        onClick: () => {
          if (!d.id) return
          // a screened drawing has results a retry will replace, so it
          // asks first; a failed one has nothing to lose
          if (!retryable && !window.confirm(
            `"${d.original_filename ?? 'This drawing'}" screened successfully.

` +
            'Re-screening replaces its existing systems with a fresh run. Continue?')) return
          void rescreenDrawing(d.id, d.original_filename)
        },
      })
      setDrawingMenu({ x: e.clientX, y: e.clientY, items })
    },
    [rescreenDrawing, rescreening],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBackToCompliance}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('projectDetail.backToCompliance')}
        </button>
        <span className="text-xs text-slate-400">{t('screening.stepTitle')}</span>
      </div>

      {promotedCount !== null && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-900/20">
          <div>
            <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
              {t('screening.promotedCount', { count: promotedCount })}
            </p>
            <p className="mt-0.5 text-xs text-emerald-700 dark:text-emerald-300">
              {t('screening.returnToCompliance')}
            </p>
          </div>
          <Button size="sm" onClick={onBackToCompliance}>
            {t('projectDetail.backToCompliance')} <ChevronRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* ── the drawings — the tab's primary content ───────────────────── */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {t('screening.wiringDiagrams')}
            {drawings.length > 0 && <span className="ml-1.5 text-xs font-normal text-slate-400">({drawings.length})</span>}
          </h3>
          {/* the blunt instrument, next to the list it acts on. The
              per-drawing button is for a fix that affected one volume;
              this is for a fix to the reader itself, where doing it one
              drawing at a time is five confirmations and an easy one to
              get half-way through. */}
          {drawings.length > 0 && runId && (
            <Button
              variant="outline"
              size="sm"
              loading={rescreening === 'ALL'}
              disabled={isScreening || rescreening !== null}
              title={isScreening
                ? t('screening.runInProgress')
                : t('screening.rescreenAllTitle')}
              onClick={() => void rescreenAll()}
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              {t('screening.rescreenAll')}
            </Button>
          )}
        </div>
        {drawings.length > 0 ? (
          <ul className="divide-y divide-slate-100 dark:divide-slate-700/60">
            {drawings.map((d, i) => (
              <li
                key={i}
                onContextMenu={e => handleDrawingContextMenu(e, d)}
                title={t('screening.rightClickActions')}
                className="flex items-center gap-2.5 py-1.5 text-xs"
              >
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="font-medium text-slate-700 dark:text-slate-200 truncate max-w-[260px]">{d.original_filename ?? t('screening.unnamed')}</span>
                {disciplineBadge(classifyDiscipline(d.original_filename ?? ''))}
                <span className="text-slate-400 tabular-nums">{(d.file_size_bytes / 1024 / 1024).toFixed(1)} MB</span>
                <span className="text-slate-400 tabular-nums">{d.page_count != null ? t('screening.pages', { count: d.page_count }) : ''}</span>
                <span className="text-slate-400">{new Date(d.uploaded_at).toLocaleDateString()}</span>
                <span className="ml-auto">
                  {d.status === 'screened'
                    ? <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"><CheckCircle className="h-3 w-3" /> {t('screening.screened')}</span>
                    : d.status === 'failed'
                      ? <span
                          title={d.fail_reason ?? undefined}
                          className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                        >{t('screening.failed')}</span>
                      /* read in full, holds nothing to assess — a circuit or
                         panel schedule. A complete outcome, not a problem, so
                         it is stated plainly rather than coloured like one. */
                      : d.status === 'no_systems'
                        ? <span
                            title={d.fail_reason ?? undefined}
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 cursor-help"
                          >{t('screening.noSystemsToAssess')}</span>
                        : <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">{d.status}</span>}
                </span>
                {/* ON EVERY ROW, not only the failed ones.
                    It used to appear on failed and empty drawings alone,
                    which meant that on four of six vessels it rendered
                    nowhere at all and the feature read as missing. The
                    right-click menu that was added instead solved
                    nothing for anyone who does not right-click.
                    Icon-only so twelve of them do not shout, with the
                    label in the tooltip. */}
                {d.id && (
                  <button
                    type="button"
                    disabled={rescreening !== null || isScreening}
                    onClick={() => {
                      const retryable = d.status === 'failed' || d.status === 'no_systems' || d.status === 'pending'
                      // a screened drawing has results a retry replaces;
                      // a failed one has nothing to lose
                      if (!retryable && !window.confirm(
                        `"${d.original_filename ?? 'This drawing'}" screened successfully.

` +
                        'Re-screening discards its systems and reads the drawing again. Continue?',
                      )) return
                      void rescreenDrawing(d.id as string, d.original_filename)
                    }}
                    className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700
                               disabled:opacity-40 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                    title={isScreening
                      ? t('screening.runInProgress')
                      : t('screening.rescreenDrawingTitle', { replace: d.status === 'screened' ? t('screening.replacesResults') : '' })}
                    aria-label={t('screening.rescreenDrawingAria', { name: d.original_filename ?? t('screening.drawing') })}
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${rescreening === d.id ? 'animate-spin' : ''}`} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('screening.uploadHint')}
          </p>
        )}
        {/* The reason, in full, under the list. It was hover-only, which
            hid the one thing that tells you whether retrying is worth it. */}
        {drawings.some(d => d.fail_reason && (d.status === 'failed' || d.status === 'no_systems')) && (
          <ul className="mt-1.5 space-y-1">
            {drawings
              .filter(d => d.fail_reason && (d.status === 'failed' || d.status === 'no_systems'))
              .map((d, i) => (
                <li key={`why-${i}`} className="pl-6 text-[11px] text-slate-500 dark:text-slate-400">
                  <span className="font-medium text-slate-600 dark:text-slate-300">
                    {d.original_filename ?? '(unnamed)'}
                  </span>
                  {' — '}
                  {d.fail_reason}
                </li>
              ))}
          </ul>
        )}
        {/* upload + screen action — always available; a project
            accumulates drawings over time */}
        <UploadPanel
          projectId={projectId}
          onRunCreated={onRunCreated}
          droppedFiles={droppedFiles}
          onDroppedConsumed={onDroppedConsumed}
          hasLiveRun={hasLiveRun}
        />
        {drawingMenu && (
          <ContextMenu
            x={drawingMenu.x}
            y={drawingMenu.y}
            items={drawingMenu.items}
            onClose={() => setDrawingMenu(null)}
          />
        )}
      </div>

      {/* ── screening results — secondary view ─────────────────────────── */}
      {!loading && runs.length === 0 && drawings.length === 0 ? null : runs.length === 0 ? (
        <p className="text-xs text-slate-500 dark:text-slate-400 px-1">
            {t('screening.screenHint')}
        </p>
      ) : (
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 px-1 -mb-1">{t('screening.results')}</h3>
      )}
      {run && run.status === 'processing' && (
        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 flex items-center gap-3 text-sm text-blue-700 dark:text-blue-300">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <div className="flex-1">
            {run.systems_total === 0
              ? t('screening.readingIndex')
              : t('screening.screeningProgress', { done: run.systems_done, total: run.systems_total })}
            <span className="ml-2 text-xs text-blue-400">
              {t('screening.serverProgress')}
            </span>
            <div className="mt-1.5 h-1.5 rounded bg-blue-100 dark:bg-blue-900/40 overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-700"
                style={{ width: run.systems_total ? `${Math.round((run.systems_done / run.systems_total) * 100)}%` : '6%' }}
              />
            </div>
          </div>
        </div>
      )}
      {failedDrawings.length > 0 && (
        <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-xs text-red-700 dark:text-red-300 space-y-1">
          <p className="font-bold flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> {t('screening.drawingsFailed')}</p>
          {failedDrawings.map((d, i) => <p key={i}>{d.original_filename ?? t('screening.unnamed')} — {d.fail_reason}</p>)}
        </div>
      )}
      {run && run.status !== 'processing' && (
        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-600 dark:text-slate-300">
          {runs.length > 1 && (
            <select
              value={runId ?? ''}
              onChange={e => setRunId(e.target.value)}
              className="text-xs border border-slate-200 dark:border-slate-600 rounded px-1.5 py-1 bg-white dark:bg-slate-800"
            >
              {runs.map(r => (
                <option key={r.id} value={r.id}>
                  {new Date(r.created_at).toLocaleString()} — {r.status} ({r.systems_done}/{r.systems_total})
                </option>
              ))}
            </select>
          )}
          <span className={
            'font-medium px-1.5 py-0.5 rounded ' +
            (run.status === 'review' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              : run.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
              : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300')
          }>{run.status}</span>
          <span>{t('screening.systemProgress', { done: run.systems_done, total: run.systems_total })}</span>
          <span className="text-slate-400">{new Date(run.created_at).toLocaleString()}</span>
          {run.error_message && <span className="text-red-500">{run.error_message}</span>}
          <div className="ml-auto flex items-center gap-2">
            {aiConfirmable > 0 && (
              <Button size="sm" variant="outline" loading={busyId === 'bulk'} onClick={bulkConfirm}>
                {t('screening.acceptAiResults', { count: aiConfirmable })}
              </Button>
            )}
            {confirmedUnpromoted > 0 && (
              <Button size="sm" loading={busyId === 'promote'} onClick={openPromotePreview}>
                {t('screening.promoteConfirmed', { count: confirmedUnpromoted })}
              </Button>
            )}
            <Button size="sm" variant="outline" loading={busyId === 'merge'} onClick={openMergePanel}
              title={t('screening.exportGroupingTitle')}>
              {t('screening.exportGrouping')}
            </Button>
            <Button size="sm" variant="outline" loading={busyId === 'export'} onClick={exportXlsx}
              title={t('screening.vendorRequestTitle')}>
              {t('screening.vendorContactRequest')}
            </Button>
          </div>
        </div>
      )}

      {/* One control for the thing people actually want, a search box,
          and everything else behind a drawer. */}
      {runs.length > 0 && (
        <div className="space-y-2 px-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border border-slate-200 dark:border-slate-600 overflow-hidden">
              {([
                ['all', `${t('screening.filters.all')} (${screeningReviewItems(allRows).length})`],
                ['target', `${t('screening.filters.target')} (${screeningReviewItems(allRows.filter(r => bucketOf(r) === 'target')).length})`],
                ['further', `${t('screening.filters.further')} (${screeningReviewItems(allRows.filter(r => bucketOf(r) === 'further')).length})`],
                ['unknown', `${t('screening.filters.notAssessed')} (${screeningReviewItems(allRows.filter(r => bucketOf(r) === 'unknown')).length})`],
                ['noip', `${t('screening.filters.noIp')} (${screeningReviewItems(allRows.filter(r => bucketOf(r) === 'noip')).length})`],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setOutcome(key)}
                  className={'px-3 py-1.5 text-xs font-medium border-r last:border-r-0 border-slate-200 dark:border-slate-600 transition-colors ' +
                    (outcome === key
                      ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700')}
                >{label}</button>
              ))}
            </div>

            <input
              value={nameQuery}
              onChange={e => setNameQuery(e.target.value)}
              placeholder={t('screening.searchPlaceholder')}
              className="text-xs px-2.5 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 w-56"
            />

            <button
              onClick={() => setShowMore(v => !v)}
              className="text-xs px-2.5 py-1.5 rounded border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
            >{showMore ? t('screening.hideFilters') : t('screening.moreFilters')}{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}</button>

            <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 cursor-pointer">
              <input type="checkbox" className="h-3.5 w-3.5 accent-blue-500" checked={showAllEvidence}
                onChange={e => { setShowAllEvidence(e.target.checked); setExpanded(e.target.checked ? new Set([...rows.map(r => r.id), ...screeningReviewItems(rows).map(item => item.id)]) : new Set()) }} />
              {t('screening.showAllEvidence')}
            </label>

            <span className={'ml-auto text-xs tabular-nums ' + (rows.length !== allRows.length ? 'font-bold text-amber-600 dark:text-amber-400' : 'text-slate-400')}>
              {t('screening.reviewItemsShown', { shown: screeningReviewItems(rows).length, total: screeningReviewItems(allRows).length })}
            </span>
            {(activeFilterCount > 0 || outcome !== 'all' || nameQuery || mismatchOnly) && (
              <button
                type="button"
                onClick={() => { clearFilters(); setOutcome('all') }}
                className="text-xs px-2 py-1 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100"
              >{t('assetInventory.clearFilters')}</button>
            )}
          </div>

          {showMore && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-slate-100 dark:border-slate-700/60">
              {MULTI_KEYS.map(key => (
                <FilterDropdown
                  key={key}
                  label={FILTER_LABEL[key]}
                  options={filterOptions[key] ?? []}
                  selected={multiFilters[key]}
                  onToggle={v => toggleFilter(key, v)}
                />
              ))}
              <label className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 cursor-pointer">
                <input type="checkbox" className="h-3 w-3 accent-slate-500" checked={mismatchOnly} onChange={e => setMismatchOnly(e.target.checked)} />
                {t('screening.titleNotesOnly')}
              </label>
            </div>
          )}
        </div>
      )}
      {promotePreview && (
        <PromoteModal
          preview={promotePreview}
          busy={busyId === 'promote'}
          onCancel={() => setPromotePreview(null)}
          onConfirm={executePromote}
        />
      )}

      {/* systems table, triage-sorted */}
      {runs.length > 0 && screeningReviewItems(rows).some(item => item.members.length > 1) && (
        <p className="text-xs text-slate-500 dark:text-slate-400">{t('screening.groupingNote')}</p>
      )}
      {runs.length > 0 && (
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-900/40 sticky top-0 z-10">
              <tr>
                {['', t('screening.columns.number'), t('fields.system'), t('fields.model'), t('screening.columns.makerSupplier'), 'CBS', t('screening.columns.shipFunction'), t('screening.columns.detail'), 'IP', t('screening.columns.connectedSystem'), t('screening.columns.target'), t('screening.columns.remark'), ''].map((h, i) => (
                  <th key={i} className="px-2 py-2 text-left font-medium text-slate-600 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
              {loading && (
                <tr><td colSpan={13} className="px-4 py-8 text-center text-slate-400">{t('common.loading')}</td></tr>
              )}
              {!loading && sections.flatMap(section => {
                if (section.rows.length === 0) return []
                const closed = closedSections.has(section.key)
                const reviewItems = screeningReviewItems(section.rows)
                const band = (
                  <tr key={`band-${section.key}`} className="bg-slate-100/80 dark:bg-slate-900/60">
                    <td colSpan={13} className="px-2 py-1.5">
                      <button
                        onClick={() => setClosedSections(prev => {
                          const next = new Set(prev)
                          if (next.has(section.key)) next.delete(section.key)
                          else next.add(section.key)
                          return next
                        })}
                        className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300"
                      >
                        {closed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        <span className={
                          section.key === 'target' ? 'text-red-700 dark:text-red-400'
                          : section.key === 'further' ? 'text-amber-700 dark:text-amber-400'
                          // not an outcome — coloured as an open question,
                          // never as the settled grey of "No IP"
                          : section.key === 'unknown' ? 'text-orange-700 dark:text-orange-400'
                          : 'text-slate-500 dark:text-slate-400'
                        }>{section.label} ({reviewItems.length})</span>
                        {section.key === 'empty' && (
                          <span className="ml-2 normal-case font-normal tracking-normal text-[11px] text-slate-500 dark:text-slate-400">
                            {t('screening.noUsableInformationDetail')}
                          </span>
                        )}
                        {section.key === 'unknown' && section.rows.length > 0 && (
                          <span className="ml-2 normal-case font-normal tracking-normal text-[11px] text-slate-500 dark:text-slate-400">
                            no IP determination was reached for these — an absent or unreadable drawing is not
                            evidence of absent IP. Open a row for the reason.
                          </span>
                        )}
                      </button>
                    </td>
                  </tr>
                )
                if (closed) return [band]
                return [band, ...reviewItems.flatMap(item => {
                const grouped = item.members.length > 1
                const groupOpen = expanded.has(item.id)
                const groupBand = (
                  <tr key={`group-${item.id}`} className="bg-blue-50/40 dark:bg-blue-900/10">
                    <td colSpan={13} className="px-3 py-2">
                      <button className="flex items-center gap-2 text-left text-slate-700 dark:text-slate-200" onClick={() => toggle(item.id)}>
                        {groupOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        <span className="font-semibold">{item.display_name}</span>
                        {item.context_varies
                          ? <span className="text-[11px] text-slate-500 dark:text-slate-400">{t('screening.individualSourceDetails')}</span>
                          : ipPill(item.members[0].ip)}
                        <span className="text-[11px] text-slate-500 dark:text-slate-400">{[item.members[0].maker, item.members[0].model].filter(Boolean).join(' · ')}</span>
                        <span className="text-[11px] font-normal text-slate-500 dark:text-slate-400">{t('screening.groupedOccurrences', { count: item.members.length })}</span>
                        {item.members.length < item.total_members && <span className="text-[10px] text-slate-400">{t('screening.filteredGroup', { total: item.total_members })}</span>}
                      </button>
                    </td>
                  </tr>
                )
                if (grouped && !groupOpen) return [groupBand]
                const memberRows = item.members.map((row, memberIndex) => {
                const isExpanded = expanded.has(row.id)
                // an unissued drawing has no AI verdict to confirm, but
                // its assessment can be entered from vendor data (edit)
                // or the row dismissed (reject)
                const actionable = row.status === 'proposed' || row.status === 'pending_maker_drawing'
                const confirmable = row.status === 'proposed' && hasUsableScreeningInformation(row)
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={
                        'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40 ' +
                        (isExpanded ? 'bg-blue-50/40 dark:bg-blue-900/10' : '')
                      }
                      onClick={() => toggle(row.id)}
                    >
                      <td className="px-2 py-1.5 w-6">
                        {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                      </td>
                      <td className="px-2 py-1.5 tabular-nums text-slate-400">{row.item_no ?? '—'}</td>
                      <td className="px-2 py-1.5 font-medium text-slate-700 dark:text-slate-200 max-w-[240px]">
                        <span className="line-clamp-1" title={row.system_name}>{row.system_name}</span>
                        {grouped && <span className="text-[10px] text-slate-400">{t('screening.sourceEntry', { index: memberIndex + 1, count: item.members.length })}</span>}
                        {row.owner_supplied && <span className="ml-1 text-[10px] text-slate-400">(O.F.E)</span>}
                        {row.scope_class === 'infrastructure' && (
                          <span className="ml-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                            title={t('screening.infrastructureTitle')}>
                            Infrastructure
                          </span>
                        )}
                        {row.scope_class === 'component' && (
                          <span className="ml-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                            title={t('screening.componentTitle')}>
                            Component
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 max-w-[140px]"><span className="line-clamp-1">{row.model ?? '—'}</span></td>
                      <td className="px-2 py-1.5 max-w-[150px]"><span className="line-clamp-1">{row.maker ?? '—'}</span></td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{row.cbs ?? '—'}</td>
                      <td className="px-2 py-1.5 max-w-[150px]"><span className="line-clamp-1">{row.ship_function ?? '—'}</span></td>
                      <td className="px-2 py-1.5 max-w-[220px]"><span className="line-clamp-2 text-slate-500">{row.detail ?? '—'}</span></td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{ipPill(row.ip)}</td>
                      <td className="px-2 py-1.5 max-w-[200px]"><span className="line-clamp-1 text-slate-500">{row.connected_system ?? '—'}</span></td>
                      <td className="px-2 py-1.5 whitespace-nowrap font-medium">
                        {row.target === 'Selected'
                          ? <span className="text-red-600 dark:text-red-400">{t('screening.selected')}</span>
                          : row.target === 'Further Review'
                            ? <span className="text-amber-600 dark:text-amber-400">{t('screening.furtherReview')}</span>
                            : <span className="text-slate-300 dark:text-slate-600">—</span>}
                      </td>
                      <td className="px-2 py-1.5 max-w-[180px]">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="line-clamp-1 text-slate-500">{row.remark ?? ''}</span>
                          {statusIcon(row.status)}
                          {row.promoted_project_system_id && (
                            <a
                              href={`/projects/${projectId}/systems/${row.promoted_project_system_id}`}
                              onClick={e => e.stopPropagation()}
                              className="text-[10px] font-medium px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 hover:underline"
                              title={t('screening.promotedTitle')}
                            >
                              in register
                            </a>
                          )}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        {actionable && (
                          <div className="flex gap-1">
                            {confirmable && (
                              <Button size="sm" variant="outline" loading={busyId === row.id}
                                onClick={() => applyPatch(row, { action: 'confirm' }, `Confirmed: ${row.system_name}`)}>
                                Confirm
                              </Button>
                            )}
                            <Button size="sm" variant={confirmable ? 'ghost' : 'outline'} onClick={() => setEditRow(row)}>
                              {confirmable ? 'Edit' : 'Enter from vendor data'}
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setRejectRow(row)}>{t('knowledge.reject')}</Button>
                          </div>
                        )}
                        {row.status === 'rejected' && row.reject_reason && (
                          <span className="text-[10px] text-slate-400 italic">{row.reject_reason}</span>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-blue-50/30 dark:bg-blue-900/5">
                        <td colSpan={13} className="px-4 py-3">
                          <div className="mb-3 rounded-md border border-blue-100 bg-white/80 px-3 py-2 dark:border-blue-900/60 dark:bg-slate-800/80">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                              Full system name
                            </div>
                            <div className="mt-0.5 whitespace-normal break-words text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {row.system_name}
                            </div>
                            {(row.maker || row.model) && (
                              <div className="mt-1 whitespace-normal break-words text-xs text-slate-500 dark:text-slate-400">
                                {[row.maker, row.model].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </div>
                          <EvidencePanel row={row} drawingName={row.drawing_id ? drawingNames.get(row.drawing_id) : undefined} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
                })
                return grouped ? [groupBand, ...memberRows] : memberRows
                })]
              })}
            </tbody>
          </table>
        </div>
        {nonSystems.length > 0 && (
          <div className="px-3 py-2 border-t border-slate-100 dark:border-slate-700/60 text-[11px] text-slate-400">
            Excluded index rows (not systems): {nonSystems.map(n => n.raw_name ?? n.system_name).join(' · ')}
          </div>
        )}
      </div>
      )}

      {editRow && (
        <EditModal
          row={editRow}
          busy={busyId === editRow.id}
          onCancel={() => setEditRow(null)}
          onSave={async fields => {
            await applyPatch(editRow, { action: 'edit', fields }, `Saved + confirmed: ${editRow.system_name}`)
            setEditRow(null)
          }}
        />
      )}
      {rejectRow && (
        <RejectModal
          row={rejectRow}
          busy={busyId === rejectRow.id}
          onCancel={() => setRejectRow(null)}
          onReject={async reason => {
            await applyPatch(rejectRow, { action: 'reject', reason }, `Rejected: ${rejectRow.system_name}`)
            setRejectRow(null)
          }}
        />
      )}

      {mergeOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setMergeOpen(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] overflow-auto p-4 space-y-3"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('screening.exportGrouping')}</h3>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                  Rows the vendor contact request combines into one line. This is export-only —
                  the screening results below are never changed, and every row keeps its own evidence.
                </p>
              </div>
              <Button size="sm" variant="outline" loading={busyId === 'merge'} onClick={reanalyse}>{t('screening.reanalyse')}</Button>
            </div>

            {mergeData?.merge_pass?.status === 'failed' && (
              <p className="text-[11px] text-red-600 dark:text-red-400">
                The last merge pass FAILED — {mergeData.merge_pass.error}. Nothing was grouped; the export
                will send every row separately.
              </p>
            )}
            {!mergeData?.merge_pass && (
              <p className="text-[11px] text-amber-700 dark:text-amber-400">
                No merge pass has run for this run. The export will send every row separately.
              </p>
            )}
            {mergeData?.merge_pass?.status === 'ok' && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {mergeData.merge_pass.groups} group(s) over {mergeData.merge_pass.considered} eligible row(s)
                · decided {mergeData.merge_pass.at?.slice(0, 16).replace('T', ' ')}
              </p>
            )}

            <div className="space-y-2">
              {(mergeData?.groups ?? []).map(g => (
                <div key={g.id} className="border border-slate-200 dark:border-slate-700 rounded p-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-200">
                        {g.display_name}
                        <span className="ml-1.5 text-[10px] text-slate-400">{g.member_ids.length} rows → 1 line</span>
                        {g.edited_by && <span className="ml-1.5 text-[10px] text-blue-500">edited by {g.edited_by}</span>}
                      </p>
                      {g.reason && <p className="text-[11px] text-slate-500 dark:text-slate-400 italic">{g.reason}</p>}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => splitGroup(g.id)}>{t('screening.split')}</Button>
                  </div>
                  <ul className="mt-1 space-y-0.5">
                    {g.member_ids.map(id => {
                      const m = (mergeData?.members ?? []).find(x => x.id === id)
                      return (
                        <li key={id} className="flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300">
                          <span className="truncate">{m?.system_name ?? id}</span>
                          <span className="text-slate-400">{m?.maker ?? ''}</span>
                          {m?.ip === 'Yes' && <span className="text-[9px] px-1 rounded bg-yellow-100 text-yellow-800">IP</span>}
                          <button className="ml-auto text-slate-400 hover:text-red-500 shrink-0"
                                  title={t('screening.removeGroupTitle')}
                                  onClick={() => dropMember(g.id, id)}>{t('screening.remove')}</button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
              {mergeData && mergeData.groups.length === 0 && mergeData.merge_pass?.status === 'ok' && (
                <p className="text-[11px] text-slate-500">{t('screening.noGrouping')}</p>
              )}
            </div>

            <div className="flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => setMergeOpen(false)}>{t('common.close')}</Button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

// ─── upload + start-run panel (Phase 5.5) ─────────────────────────────────
// chunked upload per file (kind 'ra_drawing', same three-step shape as
// the Word RA import); the "complete" step is upload-inspect, which
// reports page count + content-hash dedup WITHOUT persisting. The run
// is created — and bytes persisted — only on "Start screening".

interface UploadedFile {
  file: File
  status: 'queued' | 'uploading' | 'done' | 'error'
  progress: number          // 0–1
  selected: boolean         // include in the screening run
  discipline: Discipline    // filename-derived hint; drives the default
  session_id?: string
  page_count?: number | null
  duplicate?: boolean        // same bytes already screened on this project, in an earlier run
  content_hash?: string
  same_bytes_as?: string     // same bytes as another file in THIS batch, under a different name
  error?: string
}

function UploadPanel({ projectId, onRunCreated, droppedFiles, onDroppedConsumed, hasLiveRun }: {
  projectId: string
  onRunCreated: (runId: string) => void
  droppedFiles?: File[] | null
  onDroppedConsumed?: () => void
  hasLiveRun: boolean
}) {
  const { t } = useTranslation()
  const { success, error: showError } = useToast()
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [starting, setStarting] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [addNote, setAddNote] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const uploadingRef = useRef(false)

  const patchFile = (idx: number, patch: Partial<UploadedFile>) =>
    setFiles(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f))

  const addFiles = (list: FileList | File[]) => {
    const pdfs = Array.from(list).filter(f => f.name.toLowerCase().endsWith('.pdf'))
    if (pdfs.length === 0) { showError(t('screening.upload.pdfOnly')); return }
    const skipped: string[] = []
    setFiles(prev => {
      const seen = new Set(prev.map(f => `${f.file.name}|${f.file.size}`))
      const fresh: UploadedFile[] = []
      for (const file of pdfs) {
        // dedup on ADD: the same file reaching the queue twice (two drop
        // paths, a re-drop, a second pick) must not upload twice
        const key = `${file.name}|${file.size}`
        if (seen.has(key)) { skipped.push(file.name); continue }
        seen.add(key)
        const discipline = classifyDiscipline(file.name)
        fresh.push({
          file, status: 'queued' as const, progress: 0, discipline,
          // high-yield disciplines (and unrecognized files) default in;
          // POWER/LIGHTING default out — tickable, never hidden
          selected: !LOW_YIELD.includes(discipline),
        })
      }
      return fresh.length ? [...prev, ...fresh] : prev
    })
    // said out loud, never silently swallowed
    setAddNote(skipped.length
      ? t('screening.upload.skippedDuplicates', { count: skipped.length, names: `${skipped.slice(0, 4).join(', ')}${skipped.length > 4 ? '…' : ''}` })
      : '')
  }

  // files routed in by the page-level drop router (PDFs dropped
  // anywhere on the project page)
  useEffect(() => {
    if (droppedFiles && droppedFiles.length > 0) {
      addFiles(droppedFiles)
      onDroppedConsumed?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedFiles])

  // sequential upload worker — one file at a time (chunk level is
  // already tuned for CF; parallel files just multiply 502 exposure)
  useEffect(() => {
    if (uploadingRef.current) return
    const idx = files.findIndex(f => f.status === 'queued')
    if (idx < 0) return
    uploadingRef.current = true
    ;(async () => {
      patchFile(idx, { status: 'uploading' })
      try {
        const result = await chunkedUpload<{ session_id: string; page_count: number | null; duplicate: boolean; content_hash?: string }>({
          file: files[idx].file,
          initUrl:     `/api/projects/${projectId}/ra-screening/upload-init`,
          chunkUrl:    `/api/projects/${projectId}/ra-screening/upload-chunk`,
          completeUrl: `/api/projects/${projectId}/ra-screening/upload-inspect`,
          onProgress: (loaded, total) => patchFile(idx, { progress: total ? loaded / total : 0 }),
        })
        // second dedup layer: same BYTES under a different name. Only
        // the upload can know this, so the row stays visible (you added
        // it, you should see it) but drops out of the run.
        const twinName = result.content_hash
          ? files.find((f, i) => i !== idx && f.status === 'done' && f.content_hash === result.content_hash)?.file.name
          : undefined
        patchFile(idx, {
          status: 'done', progress: 1, session_id: result.session_id,
          page_count: result.page_count, duplicate: result.duplicate,
          content_hash: result.content_hash, same_bytes_as: twinName,
          ...(twinName ? { selected: false } : {}),
        })
      } catch (err: any) {
        patchFile(idx, { status: 'error', error: err?.message ?? t('errors.uploadFailed') })
      } finally {
        uploadingRef.current = false
        // trigger the effect again for the next queued file
        setFiles(prev => [...prev])
      }
    })()
  }, [files, projectId, t])

  const ready = files.filter(f => f.status === 'done')
  const included = ready.filter(f => f.selected)
  const totalPages = included.reduce((n, f) => n + (f.page_count ?? 0), 0)
  const estMin = Math.max(1, Math.round((totalPages * EST_SECONDS_PER_PAGE) / 60))
  const anyBusy = files.some(f => f.status === 'uploading' || f.status === 'queued')

  // an in-flight upload is browser-bound — leaving the page loses it.
  // (once the run starts, the OPPOSITE is true; the processing banner
  // says so.) beforeunload only while chunks are actually moving.
  useEffect(() => {
    if (!anyBusy) return
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [anyBusy])

  const start = async (newRun = false) => {
    setStarting(true)
    try {
      const r = await projectsApi.createScreeningRun(projectId, included.map(f => f.session_id!), newRun)
      if (r.skipped_duplicates?.length) {
        showError(t('screening.upload.alreadyIncluded', { names: r.skipped_duplicates.join('; ') }))
      }
      if (r.status === 'unchanged') {
        setFiles([]); setStarting(false); onRunCreated(r.run_id); return
      }
      if (r.attached_to_existing) success(t('screening.upload.addedToExisting', { count: r.drawings }))
      onRunCreated(r.run_id)
      setFiles([])
      setStarting(false)
    } catch (err: any) {
      const msg: string = err?.message ?? 'Failed to start screening'
      // an expired upload session is recoverable — we still hold the
      // File, so re-queue it (the worker re-uploads automatically)
      const m = /upload session ([0-9a-f-]{36}) not found, expired/i.exec(msg)
      if (m) {
        const sid = m[1]
        setFiles(prev => prev.map(f => f.session_id === sid
          ? { ...f, status: 'queued', progress: 0, session_id: undefined, page_count: undefined, duplicate: undefined }
          : f))
        showError(t('screening.upload.expired'))
      } else {
        showError(msg)
      }
      setStarting(false)
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3">
      <div
        className={
          'rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ' +
          (dragOver
            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
            : 'border-slate-300 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-700')
        }
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        // NO addFiles here. The page-level router listens on `document`
        // by design ("child components can no longer win a drop by
        // accident"), so a drop on this zone ALSO reaches it and comes
        // back through droppedFiles — handling it here too queued every
        // file twice. This handler only clears the hover state.
        onDrop={e => { e.preventDefault(); setDragOver(false) }}
      >
        <UploadCloud className="h-6 w-6 mx-auto text-slate-400 mb-1" />
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('screening.upload.dropHint')}
        </p>
        <input
          ref={inputRef} type="file" accept=".pdf" multiple className="hidden"
          onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = '' }}
        />
      </div>

      {anyBusy && (
        <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t('screening.upload.keepOpen')}
        </p>
      )}
      {addNote && (
        <p className="text-xs text-amber-600 dark:text-amber-400">{addNote}</p>
      )}
      {files.length > 0 && (
        <p className="text-xs font-medium text-slate-600 dark:text-slate-300">
          {t('screening.upload.filesAdded', { count: files.length })}
          {files.length !== ready.length && <span className="font-normal text-slate-400"> · {t('screening.upload.uploadedCount', { count: ready.length })}</span>}
        </p>
      )}
      {files.length > 0 && (
        <ul className="space-y-1.5">
          {files.map((f, i) => (
            <li key={i} className="flex items-center gap-2 text-xs">
              {f.status === 'done' && (
                <input
                  type="checkbox" checked={f.selected}
                  onChange={e => patchFile(i, { selected: e.target.checked })}
                  className="h-3.5 w-3.5 accent-blue-500"
                  aria-label={t('screening.upload.includeFile', { name: f.file.name })}
                />
              )}
              <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="font-medium text-slate-700 dark:text-slate-200 truncate max-w-[220px]">{f.file.name}</span>
              {disciplineBadge(f.discipline)}
              <span className="text-slate-400 tabular-nums">{(f.file.size / 1024 / 1024).toFixed(1)} MB</span>
              {f.status === 'uploading' && (
                <span className="flex-1 max-w-[160px] h-1.5 rounded bg-slate-100 dark:bg-slate-700 overflow-hidden">
                  <span className="block h-full bg-blue-500 transition-all" style={{ width: `${Math.round(f.progress * 100)}%` }} />
                </span>
              )}
              {f.status === 'queued' && <span className="text-slate-400">{t('screening.upload.queued')}</span>}
              {f.status === 'done' && (
                <>
                  <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                  <span className="text-slate-400 tabular-nums">{f.page_count != null ? t('screening.pages', { count: f.page_count }) : t('screening.upload.pagesUnknown')}</span>
                  {f.same_bytes_as && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                      {t('screening.upload.sameFile', { name: f.same_bytes_as })}
                    </span>
                  )}
                  {f.duplicate && !f.same_bytes_as && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      {t('screening.upload.reuseCopy')}
                    </span>
                  )}
                  {LOW_YIELD.includes(f.discipline) && !f.selected && (
                    <span className="text-[10px] text-slate-400 italic">
                      {t('screening.upload.lowYieldHint')}
                    </span>
                  )}
                </>
              )}
              {f.status === 'error' && <span className="text-red-500">{f.error}</span>}
              <button
                type="button"
                className="ml-auto text-slate-300 hover:text-red-400"
                onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                aria-label={t('common.delete')}
              >×</button>
            </li>
          ))}
        </ul>
      )}

      {ready.length > 0 && (
        <div className="flex items-center gap-3 pt-1 border-t border-slate-100 dark:border-slate-700/60">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('screening.upload.selectionSummary', { included: included.length, total: ready.length, pages: totalPages, minutes: estMin })}
          </p>
          <div className="ml-auto flex items-center gap-2">
            {/* an existing run is EXTENDED by default — the button says so,
                and the fresh-run path is explicit rather than accidental */}
            {hasLiveRun && (
              <button
                onClick={() => start(true)} disabled={anyBusy || included.length === 0 || starting}
                title={t('screening.upload.newScreeningTitle')}
                className="text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 disabled:opacity-50"
              >{t('screening.upload.newScreening')}</button>
            )}
            <Button size="sm" loading={starting} disabled={anyBusy || included.length === 0} onClick={() => start(false)}>
              {hasLiveRun ? t('screening.upload.addToScreening') : t('screening.upload.screenDrawings')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// CBS provenance. A wiring diagram shows cables — it cannot show
// whether a box contains a programmable controller, so a CBS answer the
// model INFERRED from one is a materially weaker claim than a human E26
// determination. The two must not look alike.
function cbsBadge(source?: string | null) {
  if (!source || source === 'none') return null
  const library = source === 'library'
  return (
    <span
      title={library
        ? 'From a human E26 determination in the assessment library'
        : 'Inferred by the model from the drawing — a wiring diagram cannot show whether a system contains a programmable controller'}
      className={'text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ' + (library
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
        : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500 italic border border-dashed border-slate-300 dark:border-slate-600')}
    >{library ? 'CBS: library' : 'CBS: inferred'}</span>
  )
}

function CbsProvenance({ row }: { row: ScreeningSystemRow }) {
  const { t } = useTranslation()
  const { success, error: showError } = useToast()
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const h = (row as any).cbs_history as {
    verdict?: string; matched_by?: string; weak?: boolean; conflict?: boolean
    matches?: Array<{
      system_name: string; vendor_name: string | null; model_name: string | null; is_cbs: boolean | null
      deciding?: { n: number; answer: string | null; evidence_note: string | null } | null
      source_filename?: string | null; imported_by?: string | null; imported_at?: string | null
    }>
  } | null | undefined
  const source = (row as any).cbs_source as string | undefined

  const contribute = async () => {
    setBusy(true)
    try {
      const r = await fetchWithAuthRetry(`${API_BASE_URL}/api/ra-screening/systems/${row.id}/contribute-cbs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      })
      const body = await r.json()
      if (!r.ok) { showError(body.error ?? t('screening.cbs.addFailed')); return }
      setDone(true)
      success(body.reused_profile
        ? t('screening.cbs.recordedExisting')
        : t('screening.cbs.createdProfile'))
    } catch (err: any) {
      showError(err?.message ?? t('screening.cbs.addFailed'))
    } finally { setBusy(false) }
  }

  const canContribute = ['confirmed', 'edited'].includes(row.status)
    && ['Yes', 'No'].includes(String(row.cbs))
    && !!row.maker && source !== 'library' && !done

  if (!source || source === 'none') return null
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 px-2.5 py-2 space-y-1">
      <div className="flex items-center gap-2">
        {cbsBadge(source)}
        <span className="text-slate-600 dark:text-slate-300">CBS = {row.cbs ?? '—'}</span>
        {h?.weak && <span className="text-[10px] text-amber-600 dark:text-amber-400">{t('screening.cbs.weakMatch')}</span>}
        {h?.conflict && <span className="text-[10px] text-red-600 dark:text-red-400">{t('screening.cbs.conflict')}</span>}
      </div>

      {source === 'library' && h?.matches?.length ? (
        <ul className="space-y-1">
          {h.matches.map((m, i) => (
            <li key={i} className="text-[11px] text-slate-500 dark:text-slate-400">
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {m.is_cbs ? 'CBS' : 'not CBS'}
              </span>
              {' — '}“{m.system_name}”{m.vendor_name ? ` · ${m.vendor_name}` : ''}{m.model_name ? ` · ${m.model_name}` : ''}
              {m.deciding?.evidence_note && <> · <span className="italic">Q{m.deciding.n}: {m.deciding.evidence_note}</span></>}
              {m.imported_by && <> · recorded by {m.imported_by}</>}
              {m.imported_at && <> on {m.imported_at.slice(0, 10)}</>}
            </li>
          ))}
        </ul>
      ) : source === 'inferred' ? (
        <p className="text-[11px] text-slate-400">
          {t('screening.cbs.inferredWarning')}
        </p>
      ) : null}

      {canContribute && (
        <button
          onClick={contribute} disabled={busy}
          className="text-[11px] px-2 py-0.5 rounded border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50"
        >{busy ? t('screening.cbs.adding') : t('screening.cbs.addDetermination')}</button>
      )}
      {done && <p className="text-[11px] text-emerald-600 dark:text-emerald-400">{t('screening.cbs.added')}</p>}
    </div>
  )
}

// The cited page, rendered INLINE beneath its citation — the reviewer
// compares quote to drawing without losing their place in the table.
//
// The page number comes straight from the evidence entry, which already
// holds the RESOLVED pdf page (screening cites the page it actually
// rendered, not the printed sheet). Nothing is re-derived here — a
// viewer that resolved the page a second way could disagree with the
// citation it is meant to show, which would be worse than no viewer.
function InlinePagePreview({ drawingId, drawingName, citations, index, onIndex, onClose }: {
  drawingId: string
  drawingName: string
  citations: Array<{ page: number; cable_type?: string | null; interface_device?: string | null; quote: string }>
  index: number
  onIndex: (i: number) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  // 1 = the WHOLE page fits in the container. Fit-to-width looked
  // right on paper and was wrong in practice: an A4 sheet at 150 DPI is
  // 1754px tall, so in a 65vh window it showed roughly a quarter of the
  // drawing with no indication the rest existed.
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [loading, setLoading] = useState(true)
  const [imgUrl, setImgUrl] = useState<string | null>(null)
  const [imgError, setImgError] = useState<string | null>(null)
  const dragRef = useRef<{ x: number; y: number } | null>(null)
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const page = citations[index]?.page

  // panning only means something once zoomed in. At fit, dragging would
  // just push the whole page off into the empty stage — which is exactly
  // what made it look "cut".
  const zoomRef = useRef(zoom)
  useEffect(() => {
    zoomRef.current = zoom
    if (zoom <= 1) setPan({ x: 0, y: 0 })
  }, [zoom])
  const panBy = (dx: number, dy: number) => {
    if (zoomRef.current <= 1) return
    setPan(p => ({ x: p.x + dx, y: p.y + dy }))
  }
  const panTo = (x: number, y: number) => {
    if (zoomRef.current <= 1) return
    setPan({ x, y })
  }

  // a different page starts fresh — inherited pan would land the
  // reviewer somewhere arbitrary on a different sheet
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); setLoading(true) }, [index])

  // the render endpoint is admin-gated and an <img src> cannot carry the
  // auth header, so the PNG is fetched and handed over as an object URL
  useEffect(() => {
    if (!drawingId || page == null) return
    let url: string | null = null
    let cancelled = false
    setImgError(null)
    ;(async () => {
      try {
        const r = await fetchWithAuthRetry(`${API_BASE_URL}/api/ra-screening/drawings/${drawingId}/page/${page}`, { method: 'GET' })
        if (!r.ok) {
          const body = await r.json().catch(() => ({}))
          if (!cancelled) { setImgError(body.error ?? `could not render page ${page}`); setLoading(false) }
          return
        }
        const blob = await r.blob()
        url = URL.createObjectURL(blob)
        if (cancelled) { URL.revokeObjectURL(url); return }
        setImgUrl(url)
      } catch (err: any) {
        if (!cancelled) { setImgError(err?.message ?? 'could not load the page'); setLoading(false) }
      }
    })()
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url) }
  }, [drawingId, page, t])

  // WHEEL: React registers wheel as PASSIVE at the root, so
  // preventDefault inside onWheel is silently ignored and the page
  // behind scrolls anyway. A native non-passive listener is the only
  // way to actually stop it.
  //   plain wheel  → pan (an A3 sheet does not fit this window)
  //   shift+wheel  → pan horizontally
  //   ctrl/⌘+wheel → zoom (also what a trackpad pinch sends)
  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      if (e.ctrlKey || e.metaKey) {
        setZoom(z => Math.min(6, Math.max(1, z - e.deltaY * 0.002 * z)))
        return
      }
      panBy(e.shiftKey ? -e.deltaY : -e.deltaX, e.shiftKey ? 0 : -e.deltaY)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight' && index < citations.length - 1) onIndex(index + 1)
      if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, citations.length, onIndex, onClose])

  if (page == null) return null

  return (
    <div className="mt-1 mb-2 rounded border border-slate-300 dark:border-slate-600 overflow-hidden">
      <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-700">
        <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{drawingName} · {t('common.page')} {page}</span>
        {citations.length > 1 && (
          <span className="flex items-center gap-1">
            <button onClick={() => onIndex(index - 1)} disabled={index === 0}
              className="px-1.5 rounded border border-slate-300 dark:border-slate-600 disabled:opacity-40" aria-label={t('screening.previousCitation')}>←</button>
            <span className="text-[10px] tabular-nums text-slate-400">{index + 1}/{citations.length}</span>
            <button onClick={() => onIndex(index + 1)} disabled={index === citations.length - 1}
              className="px-1.5 rounded border border-slate-300 dark:border-slate-600 disabled:opacity-40" aria-label={t('screening.nextCitation')}>→</button>
          </span>
        )}
        <span className="ml-auto flex items-center gap-1 text-[10px]">
          <button onClick={() => setZoom(z => Math.max(1, z - 0.25))} className="px-1.5 rounded border border-slate-300 dark:border-slate-600">−</button>
          <span className="tabular-nums text-slate-400 w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(6, z + 0.25))} className="px-1.5 rounded border border-slate-300 dark:border-slate-600">+</button>
          <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }) }} className="px-1.5 rounded border border-slate-300 dark:border-slate-600">{t('screening.fit')}</button>
          {imgUrl && (
            // the blob is already in hand, so "larger" is a new tab for free
            <button onClick={() => window.open(imgUrl, '_blank', 'noopener')}
              className="px-1.5 rounded border border-slate-300 dark:border-slate-600">{t('screening.openLarger')}</button>
          )}
          <button onClick={onClose} className="px-1.5 rounded bg-slate-200 dark:bg-slate-700">{t('common.close')}</button>
        </span>
      </div>

      <div
        ref={stageRef}
        // overscroll containment stops panning at an edge from scrolling
        // the systems table; touchAction none keeps the browser from
        // panning the PAGE when the gesture is meant for the drawing
        style={{ height: '70vh', overscrollBehavior: 'contain', touchAction: 'none' }}
        className="relative overflow-hidden bg-slate-800 cursor-grab active:cursor-grabbing flex items-center justify-center"
        onMouseDown={e => { dragRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y } }}
        onMouseMove={e => { if (dragRef.current) panTo(e.clientX - dragRef.current.x, e.clientY - dragRef.current.y) }}
        onMouseUp={() => { dragRef.current = null }}
        onMouseLeave={() => { dragRef.current = null }}
        onTouchStart={e => {
          if (e.touches.length === 2) {
            const [a, b] = [e.touches[0], e.touches[1]]
            pinchRef.current = { dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), zoom }
            dragRef.current = null
          } else if (e.touches.length === 1) {
            dragRef.current = { x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y }
          }
        }}
        onTouchMove={e => {
          if (e.touches.length === 2 && pinchRef.current) {
            const [a, b] = [e.touches[0], e.touches[1]]
            const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
            setZoom(Math.min(6, Math.max(1, pinchRef.current.zoom * (dist / (pinchRef.current.dist || 1)))))
          } else if (e.touches.length === 1 && dragRef.current) {
            panTo(e.touches[0].clientX - dragRef.current.x, e.touches[0].clientY - dragRef.current.y)
          }
        }}
        onTouchEnd={() => { dragRef.current = null; pinchRef.current = null }}
      >
        {loading && !imgError && <p className="p-3 text-[11px] text-slate-300">{t('screening.renderingPage', { page })}</p>}
        {imgError && <p className="p-3 text-[11px] text-red-300">{imgError}</p>}
        {imgUrl && (
          <img
            src={imgUrl} alt={`${drawingName} page ${page}`} draggable={false}
            onLoad={() => setLoading(false)}
            // max-w/max-h letterbox the page to fit whole; zoom then
            // magnifies from the centre, which is where the eye already is
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: 'center center' }}
            className="max-w-full max-h-full object-contain select-none"
          />
        )}
      </div>
      <p className="px-2 py-0.5 text-[10px] text-slate-400 bg-slate-50 dark:bg-slate-900/60">
        {t('screening.previewHelp')}
      </p>
    </div>
  )
}

// the IP basis survives here, off the main row: five different reasons
// all read as "No" in the table, and the distinction still matters to
// anyone diagnosing a vocabulary gap — internal_ip_only especially,
// which means Ethernet exists but never leaves the system
const BASIS_NOTE: Record<string, string> = {
  explicit_ip: 'IP vocabulary cited on the drawing.',
  internal_ip_only: 'Ethernet exists inside this system but does not leave it — no IP link to another system.',
  explicit_serial: 'Serial data link cited — not IP.',
  explicit_hardwired: 'Hardwired control/power wiring cited — not IP.',
  no_data_connections: 'No data connections stated on the drawing.',
  default_serial_untyped_pair: 'Resolved by rule: twisted pair with no protocol stated on the drawing.',
  unclassifiable: 'Needs human eyes — the evidence did not settle it.',
}
function BasisNote({ basis }: { basis: string }) {
  return (
    <p className="flex items-center gap-2">
      {basisBadge(basis)}
      <span className="text-slate-500 dark:text-slate-400">{BASIS_NOTE[basis] ?? basis}</span>
    </p>
  )
}

// evidence verbatim — the whole point of the review: the reviewer reads
// what the drawing says, not a label
function EvidencePanel({ row, drawingName }: { row: ScreeningSystemRow; drawingName?: string }) {
  const { t } = useTranslation()
  const [viewing, setViewing] = useState<number | null>(null)
  const citations = row.evidence ?? []
  return (
    <div className="space-y-2 text-xs">
      {hasTitleAssociationNote(row) && (
        <details className="rounded border border-slate-200 dark:border-slate-700 px-2.5 py-2 text-slate-500 dark:text-slate-400">
          <summary className="cursor-pointer font-medium">{t('screening.evidence.titleAssociationNote')}</summary>
          <p className="mt-1">{t('screening.evidence.titleAssociationDetail', { expected: row.system_name, actual: row.title_block_system ?? t('screening.evidence.unreadable') })}</p>
          <p className="mt-1">{t('screening.evidence.titleAssociationCaution')}</p>
        </details>
      )}
      {row.scope_class === 'infrastructure' && (
        <p className="text-indigo-600 dark:text-indigo-400">
          {t('screening.evidence.infrastructure')}
        </p>
      )}
      {row.detail && <p className="text-slate-600 dark:text-slate-300">{row.detail}</p>}
      {!row.ip && (
        <p className="text-violet-600 dark:text-violet-400">
          {t('screening.evidence.notAssessed')}
          {' '}{notAssessedReason(row)}
        </p>
      )}
      {row.anchor_note && (
        <p className="text-slate-500 dark:text-slate-400">Anchoring: {row.anchor_note}</p>
      )}
      {/* the yard has answered for this system — visible without
          leaving the screening tab, and honest that it is not yet a
          register link */}
      {row.pending_contacts && row.pending_contacts.length > 0 && (
        <div className="rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/20 px-2 py-1.5">
          <p className="text-[11px] font-medium text-emerald-800 dark:text-emerald-300">
            {t('screening.evidence.contactsSupplied', { count: row.pending_contacts.length })}
          </p>
          <ul className="mt-0.5 space-y-0.5">
            {row.pending_contacts.map(c => (
              <li key={c.id} className="text-[11px] text-emerald-900/80 dark:text-emerald-200/80">
                {c.full_name ?? '(no name)'}
                {c.email && <span className="ml-1.5 text-emerald-700 dark:text-emerald-400">{c.email}</span>}
                {c.phone && <span className="ml-1.5 text-slate-500">{c.phone}</span>}
                {c.company_name && <span className="ml-1.5 text-slate-400">{c.company_name}</span>}
              </li>
            ))}
          </ul>
          <p className="mt-0.5 text-[10px] text-emerald-700/70 dark:text-emerald-400/70">
            {t('screening.evidence.contactsHeld')}
          </p>
        </div>
      )}
      <CbsProvenance row={row} />
      {row.ip_basis && <BasisNote basis={String(row.ip_basis)} />}

      {row.status === 'failed' && row.fail_reason && (
        <p className="text-red-500">{t('screening.evidence.failed')}: {row.fail_reason}</p>
      )}
      {row.evidence?.length ? (
        <ul className="space-y-0.5">
          {row.evidence.map((e, i) => {
            // the citation itself is the label; the sheet it came from is
            // secondary but never hidden — the reviewer should know which
            // page they are about to open
            const body = (
              <>
                <span className="flex items-baseline gap-2">
                  <span className="font-mono font-medium text-slate-700 dark:text-slate-200">{e.cable_type ?? '—'}</span>
                  {e.interface_device && (
                    <span className="text-slate-700 dark:text-slate-200 truncate">→ {e.interface_device}</span>
                  )}
                  <span className="ml-auto shrink-0 text-[10px] tabular-nums text-slate-400">page {e.page}</span>
                </span>
                <span className="block text-slate-500 dark:text-slate-400">“{e.quote}”</span>
              </>
            )
            return (
              <li key={i}>
                {row.drawing_id ? (
                  // a real button, so Enter/Space and focus come for free
                  <button
                    onClick={() => setViewing(v => v === i ? null : i)}
                    aria-expanded={viewing === i}
                    title={viewing === i ? t('screening.evidence.hidePage') : t('screening.evidence.showPage', { page: e.page })}
                    className={'w-full text-left rounded px-2 py-1.5 cursor-pointer transition-colors ' +
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ' +
                      (viewing === i
                        ? 'bg-blue-50 dark:bg-blue-900/25'
                        : 'hover:bg-blue-50/70 dark:hover:bg-blue-900/20')}
                  >{body}</button>
                ) : (
                  <div className="px-2 py-1.5">{body}</div>
                )}
                {viewing === i && row.drawing_id && (
                  <InlinePagePreview
                    drawingId={row.drawing_id}
                    drawingName={drawingName ?? t('screening.drawing')}
                    citations={citations}
                    index={i}
                    onIndex={setViewing}
                    onClose={() => setViewing(null)}
                  />
                )}
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="text-slate-400 italic">{t('screening.evidence.empty')}</p>
      )}
      {row.ai_original && (
        <details className="text-[11px] text-slate-400">
          <summary className="cursor-pointer">{t('screening.evidence.originalValues')}</summary>
          <pre className="mt-1 whitespace-pre-wrap rounded bg-slate-50 dark:bg-slate-900/40 p-2">{JSON.stringify(row.ai_original, null, 2)}</pre>
        </details>
      )}
    </div>
  )
}

function EditModal({ row, busy, onCancel, onSave }: {
  row: ScreeningSystemRow
  busy: boolean
  onCancel: () => void
  onSave: (fields: Partial<Record<EditKey, string>>) => void
}) {
  const { t } = useTranslation()
  const [values, setValues] = useState<Record<EditKey, string>>(() =>
    Object.fromEntries(EDIT_FIELDS.map(f => [f.key, (row[f.key] as string | null) ?? ''])) as Record<EditKey, string>)

  const changed: Partial<Record<EditKey, string>> = {}
  for (const f of EDIT_FIELDS) {
    if (values[f.key] !== ((row[f.key] as string | null) ?? '')) changed[f.key] = values[f.key]
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg p-4 space-y-3" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('common.edit')} — {row.system_name}</h3>
        <p className="text-[11px] text-slate-400">{t('screening.editHelp')}</p>
        <div className="grid grid-cols-2 gap-2 max-h-[55vh] overflow-y-auto pr-1">
          {EDIT_FIELDS.map(f => (
            <label key={f.key} className={'text-[11px] text-slate-500 dark:text-slate-400 ' + (f.type === 'textarea' ? 'col-span-2' : '')}>
              {t(`screening.editFields.${f.labelKey}`)}
              {f.type === 'select' ? (
                <select
                  value={values[f.key]}
                  onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                  className="mt-0.5 w-full text-xs border border-slate-200 dark:border-slate-600 rounded px-1.5 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                >
                  {['Yes', 'No', 'Further Review'].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === 'textarea' ? (
                <textarea
                  value={values[f.key]}
                  onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                  rows={2}
                  className="mt-0.5 w-full text-xs border border-slate-200 dark:border-slate-600 rounded px-1.5 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                />
              ) : (
                <input
                  value={values[f.key]}
                  onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                  className="mt-0.5 w-full text-xs border border-slate-200 dark:border-slate-600 rounded px-1.5 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                />
              )}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button size="sm" loading={busy} disabled={Object.keys(changed).length === 0}
            onClick={() => onSave(changed)}>
            {t('screening.saveConfirm')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function PromoteModal({ preview, busy, onCancel, onConfirm }: {
  preview: Awaited<ReturnType<typeof projectsApi.promoteScreening>>
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg p-4 space-y-3" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('screening.promoteTitle')}</h3>
        <div className="text-xs text-slate-600 dark:text-slate-300 space-y-2 max-h-[55vh] overflow-y-auto pr-1">
          <p>
            <span className="font-semibold text-green-600 dark:text-green-400">{preview.created} new</span> register system(s) will be created ·{' '}
            <span className="font-semibold text-blue-600 dark:text-blue-400">{preview.updated} existing</span> will be updated fill-blanks
            (populated fields are never overwritten; system ids unchanged).
          </p>
          {preview.to_create.length > 0 && (
            <details><summary className="cursor-pointer font-medium">{t('screening.toCreate')} ({preview.to_create.length})</summary>
              <ul className="mt-1 list-disc pl-4 text-slate-500">{preview.to_create.map((n, i) => <li key={i}>{n}</li>)}</ul>
            </details>
          )}
          {preview.to_update.length > 0 && (
            <details><summary className="cursor-pointer font-medium">{t('screening.toUpdate')} ({preview.to_update.length})</summary>
              <ul className="mt-1 list-disc pl-4 text-slate-500">{preview.to_update.map((u, i) => <li key={i}>{u.screening} → {u.existing}</li>)}</ul>
            </details>
          )}
          {preview.ambiguous.length > 0 && (
            <div className="rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-2 text-amber-700 dark:text-amber-300">
              <p className="font-bold">{preview.ambiguous.length} ambiguous — skipped, never guessed:</p>
              {preview.ambiguous.map((a, i) => <p key={i}>{a.screening}: matches {a.candidates.join(' / ')}</p>)}
            </div>
          )}
          <p className="text-slate-400">
            Excluded by rule: {preview.skipped_component} component(s) (parts of parent systems) ·{' '}
            {preview.skipped_infrastructure} infrastructure (assessed via connected systems) ·{' '}
            {preview.not_confirmed} not yet confirmed · {preview.skipped_already} already promoted.
            IP / model / connected-system / Target stay on the screening rows, linked to their register systems.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button size="sm" loading={busy} disabled={preview.created + preview.updated === 0} onClick={onConfirm}>
            {t('screening.promoteSystems', { count: preview.created + preview.updated })}
          </Button>
        </div>
      </div>
    </div>
  )
}

function RejectModal({ row, busy, onCancel, onReject }: {
  row: ScreeningSystemRow
  busy: boolean
  onCancel: () => void
  onReject: (reason?: string) => void
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-sm p-4 space-y-3" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('knowledge.reject')} — {row.system_name}</h3>
        <label className="text-[11px] text-slate-500 dark:text-slate-400 block">
          {t('screening.rejectReason')} ({t('common.optional')})
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            className="mt-0.5 w-full text-xs border border-slate-200 dark:border-slate-600 rounded px-1.5 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
            placeholder={t('screening.rejectPlaceholder')}
          />
        </label>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button size="sm" variant="destructive" loading={busy} onClick={() => onReject(reason || undefined)}>{t('knowledge.reject')}</Button>
        </div>
      </div>
    </div>
  )
}
